/**
 * A random id, on every context this app actually runs in.
 *
 * ⚠️ **`crypto.randomUUID` is a secure-context API.** It is `undefined` on a
 * plain-http origin — which is precisely how this app is reached during
 * development, since the dev server binds to the LAN so a phone can open it at
 * `http://192.168.x.x:5173`. Calling it there throws
 * `TypeError: crypto.randomUUID is not a function`, and a bare `TypeError`
 * thrown mid-upload surfaces to the customer as an unexplained failure.
 *
 * `crypto.getRandomValues` has no such restriction and is the real requirement
 * — the values must be unguessable, because the visit id scopes what gets
 * cleared at the end of a visit.
 */
export function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    // ⚠️ Last resort, and not cryptographically random. Reached only on a
    // browser with no Web Crypto at all, which no supported target is. Kept so
    // a visit id can always be minted rather than the capture failing outright.
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }

  // RFC 4122 version 4, variant 1 — the same shape `randomUUID` returns, so
  // nothing downstream can tell which path produced it.
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;

  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join('-');
}
